import React, { createContext, useContext, useState } from 'react'
import { FeedItem } from '../../Api/Feed/feed.types'
import { sendGamificationEngagement } from '../../Api/gamification/gamification.api'
import { Overlay } from '../../Components/Layout'
import {
  sendVideoComplete,
  sendVideoPause,
  sendVideoStart,
} from '../../Utils/Video/videoUtils'

import { VideoPlayer } from '../../Components/Videos/VideoPlayer'
import { logBrazeEvent } from '../../Utils/analytics/brazeAnalytics'
import { AuthContext } from '../../Api/firebase'
import { gamificationEngagements } from '../../Constants/gamification'
import { getVideoPlayerOptions } from '../../Constants/video'

type VideoContextType = {
  loadAndShowVideo: (mediaItem: FeedItem) => void
  loading: boolean
}

export const VideoContext = createContext<VideoContextType | null>(null)

export const VideoProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { isLoggedIn, userId } = useContext(AuthContext)!
  const [mediaItem, setMediaItem] = useState<FeedItem | null>(null)
  const [showVideo, setShowVideo] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [watchCredit, setWatchCredit] = useState<boolean>(false)
  // ANALYTICS  //
  const [videoPlayCount, setVideoPlayCounter] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)

  const loadAndShowVideo = (media: FeedItem) => {
    setMediaItem(media)
    setDuration(media?.duration || 0)
    setShowVideo(!showVideo)
    setLoading(false)
  }
  const handleClose = () => {
    setMediaItem(null)
    watchCredit && setWatchCredit(false)
    setShowVideo(!showVideo)
  }

  // ANALYTICS  //
  const logVideoComplete = () => {
    //Logs 85% completion
    let params = {
      videoLength: duration > 0 ? duration : '',
      mediaId: mediaItem?.mediaId,
    }
    sendVideoComplete(params)
  }

  const onVideoStart = () => {
    if (videoPlayCount > 0) return
    let params = {
      videoLength: duration > 0 ? duration : '',
      mediaId: mediaItem?.mediaId,
    }
    setVideoPlayCounter(1)
    sendVideoStart(params)
    if (!watchCredit && mediaItem?.mediaId) {
      if (isLoggedIn && userId) {
        setWatchCredit(true)
        Promise.resolve(
          sendGamificationEngagement(
            userId,
            gamificationEngagements.VIDEO_WATCH,
            mediaItem.mediaId
          )
        )
      }
      logBrazeEvent(gamificationEngagements.VIDEO_WATCH, params)
    }
  }

  const onVideoPause = (watchedTime: number) => {
    if (watchedTime === 0 || duration === 0) return
    let pctWatched = watchedTime / duration
    let params = {
      mediaId: mediaItem?.mediaId,
      seconds_watched: watchedTime,
      percentage_watched: pctWatched,
    }
    sendVideoPause(params)
  }

  return (
    <VideoContext.Provider
      value={{
        loading,
        loadAndShowVideo,
      }}
    >
      {children}
      {showVideo && mediaItem?.mediaId && (
        <Overlay type="video" onClick={handleClose}>
          <VideoPlayer
            key={mediaItem.mediaId}
            videoId={mediaItem.mediaId}
            options={getVideoPlayerOptions({
              autoplay: true,
              aspectRatio: mediaItem?.aspectRatio ?? '16:9',
              muted: false,
            })}
            onVideoStart={onVideoStart}
            logVideoComplete={logVideoComplete}
            onVideoPause={onVideoPause}
            aria-label={`Video player for ${mediaItem.mediaId}`}
          />
        </Overlay>
      )}
    </VideoContext.Provider>
  )
}
